import { useCallback, useEffect, useState, type JSX } from 'react';
import { useParams } from '@tanstack/react-router';
import type { FinanceEditBankRequest, FinanceValidatePage } from '@procuai/contracts';
import { BankValidate, type BankValidateAdapter, type FinanceValidatePageView } from '@procuai/ui';
import { api, ApiError } from '../api.js';

/**
 * The public bank-validation page (D-141). Like the supplier portal, it lives OUTSIDE the authenticated
 * app shell: the supplier's appointed finance colleague is not a ProcuAI user and opens it from their own
 * emailed token link. It reads GET /portal/bank-validate/:token and either validates
 * (POST .../validate) or corrects (PUT .../) the bank details. An invalid or expired token (404) renders
 * the friendly error state in the BankValidate component; no app data is ever shown.
 */
function toView(p: FinanceValidatePage): FinanceValidatePageView {
  return {
    orgName: p.orgName,
    supplierName: p.supplierName,
    financeEmail: p.financeEmail,
    validationStatus: p.validationStatus,
    iban: p.iban,
    bic: p.bic,
    bankName: p.bankName,
    accountHolder: p.accountHolder,
  };
}

export function BankValidatePage(): JSX.Element {
  const { token } = useParams({ from: '/portal/bank-validate/$token' });
  const [page, setPage] = useState<FinanceValidatePageView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api<FinanceValidatePage>(`/portal/bank-validate/${token}`)
      .then((p) => { if (live) setPage(toView(p)); })
      .catch((e: unknown) => { if (live) setError(e instanceof ApiError ? e.message : 'This link is invalid or has expired.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [token]);

  const validate = useCallback(async () => {
    const p = await api<FinanceValidatePage>(`/portal/bank-validate/${token}/validate`, { method: 'POST' });
    setPage(toView(p));
  }, [token]);

  const editBank = useCallback(async (input: FinanceEditBankRequest) => {
    const p = await api<FinanceValidatePage>(`/portal/bank-validate/${token}`, { method: 'PUT', body: JSON.stringify(input) });
    setPage(toView(p));
  }, [token]);

  const adapter: BankValidateAdapter = { page, loading, error, validate, editBank };
  return <BankValidate adapter={adapter} />;
}
